import { useFormikContext } from "formik";
import type { FocusEvent, KeyboardEvent } from "react";
import { useCallback, useMemo } from "react";

import { useFormContext } from "../FormContext";
import { useOptionalFieldContext } from "../FormFieldContext";
import { ErrorIcon, PendingIcon } from "./icons";
import type { AutocompleteOption, Option } from "./types";

type BlurOnEnterKeyProps = {
  onEnterKey?: () => void;
};

export const useBlurOnEnterKey = (props?: BlurOnEnterKeyProps) =>
  useCallback(
    (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        event.currentTarget.blur();
        props?.onEnterKey?.();
      }
    },
    [props],
  );

export const useHandleBlur = () => {
  const { submitOnBlur } = useFormContext();
  const { dirty, submitForm, handleBlur } = useFormikContext();

  return useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleBlur(event);
      if (submitOnBlur && dirty) {
        void submitForm(); // this returns a promise which rejects if validation fails
      }
    },
    [dirty, handleBlur, submitForm, submitOnBlur],
  );
};

export const useCommitChange = () => {
  const { submitOnBlur } = useFormContext();
  const { submitForm } = useFormikContext();

  return useCallback(() => {
    if (submitOnBlur) {
      // note: the form may not be marked as dirty at this point
      //       but will be submitted nonetheless
      void submitForm(); // this returns a promise which rejects if validation fails
    }
  }, [submitForm, submitOnBlur]);
};

// Check to see if the target element is a user input. Useful to check if the user is in the middle of typing.
export const isUserInput = (el: EventTarget | Element | null) => {
  const element = el as Element;

  if (!element) return false;

  if ((element as HTMLInputElement).readOnly) return false;

  if (["INPUT", "TEXTAREA", "BUTTON", "SELECT", "OPTION"].includes(element.tagName)) return true;

  if (element.id === "fade-menu") return true;

  // note: inside web-object editor
  if (element.getAttribute("class")?.includes("native-edit-context")) return true;

  return false;
};

export const isInsideContentEditable = (target: EventTarget | Element | null) => {
  return target instanceof Element && target?.closest("[contentEditable]");
};

export const toSpaceCase = (input: string) => {
  return (
    input
      .replace(/[-_]/g, " ")
      /*
       * insert a space between lower & upper
       * HttpRequest => Http Request
       */
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      /*
       * space before last upper in a sequence followed by lower
       * XMLHttp => XML Http
       */
      .replace(/\b([A-Z]+)([A-Z])([a-z])/, "$1 $2$3")
      // uppercase the first character
      .replace(/^./, (val) => val.toUpperCase())
      .replace(/\s+/g, " ")
      .trim()
  );
};

export const optionize = (values: string[]): AutocompleteOption[] =>
  values.map((value) => ({ label: value, value })) as AutocompleteOption[];

export const DIVIDER_OPTION_VALUE = "@@divider";

// have to return undefined instead of "" for no value or MUI will think it's a label
export const useFieldLabelAndDescription = (name: string) => {
  const { formatMessage, explicitLabels } = useFormContext();
  const context = useOptionalFieldContext();

  if (import.meta.env.NODE_ENV !== "production") {
    if (!formatMessage && (explicitLabels !== undefined || context?.explicitLabel !== undefined)) {
      console.error(
        `Setting explicitLabel/explictLabels only relevant on forms with formatStrings setting. Field ${name}.`,
      );
    }
  }

  return useMemo(() => {
    if (!formatMessage || explicitLabels || context?.explicitLabel) return [undefined, undefined];

    const primaryKey = name.split(".")[0];
    const nestedKey = name.split(".")[1];

    const formattedLabel = formatMessage(`${primaryKey}Label`);

    const label = nestedKey ? `${formattedLabel}: ${formatMessage(nestedKey)}` : formattedLabel;
    const description = formatMessage(`${primaryKey}Description`) || undefined;

    return [label, description];
  }, [context?.explicitLabel, explicitLabels, formatMessage, name]);
};

export const useFieldPlaceholder = (name: string) => {
  const { formatMessage } = useFormContext();

  return useMemo(() => formatMessage?.(`${name}Placeholder`), [formatMessage, name]);
};

export const createDividerOption = (group = "") => ({
  label: `${DIVIDER_OPTION_VALUE}-${group}`,
  value: DIVIDER_OPTION_VALUE,
  group,
  disabled: true,
});

/**
 * Interleaves a group-header divider option at each group transition, for Select-based
 * pickers. Expects options already contiguous by `group`; ungrouped options ("") get
 * no header, so they belong at the front to avoid attaching to the previous header.
 */
export const withGroupDividers = (options: readonly Option[]): Option[] =>
  options.flatMap((option, index) => {
    const group = option.group ?? "";
    const previousGroup = index === 0 ? "" : (options[index - 1].group ?? "");
    return group && group !== previousGroup ? [createDividerOption(group), option] : [option];
  });

export const selectFieldIconComponent = (status: "pending" | "error" | "success") => {
  if (status === "pending") return PendingIcon;
  if (status === "error") return ErrorIcon;
};
